import React from "react";
import { Menu, MenuItem, Typography } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { Message } from "../../../../../shared/types/chatWindow.types";

interface MessageMenuProps {
  anchorEl: null | HTMLElement;
  activeMessage: Message | null;
  onClose: () => void;
}

const MessageMenu: React.FC<MessageMenuProps> = ({ anchorEl, activeMessage, onClose }) => {
  const hasMarkdownTable = (text: string) => {
    return text.includes("|") && text.includes("---");
  };

  const handleDownloadPDF = () => {
    if (!activeMessage) return;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const fileId = activeMessage.threadId || activeMessage.id.substring(0, 6);
    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const maxLineWidth = pageWidth - margin * 2;
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);

    let currentY = margin + 5;
    let textBuffer: string[] = [];
    let tableBuffer: string[] = [];

    const flushText = () => {
      if (textBuffer.length === 0) return;
      
      const cleanText = textBuffer.join("\n").replace(/[*#]/g, "");
      const textLines = doc.splitTextToSize(cleanText, maxLineWidth);
      
      if (currentY + (textLines.length * 5) > pageHeight - margin) {
        doc.addPage();
        currentY = margin + 5;
      }

      doc.text(textLines, margin, currentY);
      currentY += textLines.length * 5 + 5;
      textBuffer = [];
    };

    const flushTable = () => {
      if (tableBuffer.length === 0) return;

      const aoa = tableBuffer.map((line) =>
        line
          .split("|")
          .map((cell) => cell.trim())
          .filter((_, index, arr) => index !== 0 && index !== arr.length - 1)
      );

      const head = aoa.length > 0 ? [aoa[0]] : [];
      const body = aoa.filter((row, idx) => idx !== 0 && !row.every((cell) => /^[-:]+$/.test(cell)));

      autoTable(doc, {
        startY: currentY,
        head: head,
        body: body,
        margin: { left: margin, right: margin },
        theme: "grid",
        headStyles: { fillColor: [7, 56, 87] }, 
        styles: { fontSize: 10, cellPadding: 3 },
      });

      currentY = (doc as any).lastAutoTable.finalY + 8;
      tableBuffer = [];
    };

    const lines = activeMessage.text.split("\n");
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      if (line.startsWith("|") && line.endsWith("|")) {
        flushText();
        tableBuffer.push(line);
      } else {
        flushTable();
        if (line !== "") {
          textBuffer.push(line);
        }
      }
    }

    flushText();
    flushTable();

    doc.save(`HACS_Response_${fileId}.pdf`);
    onClose();
  };

  const handleDownloadXLSX = () => {
    if (!activeMessage) return;

    const lines = activeMessage.text.split("\n");
    const tableLines = lines.filter(
      (line) => line.trim().startsWith("|") && line.trim().endsWith("|")
    );

    if (tableLines.length === 0) {
      onClose();
      return;
    }

    const aoa = tableLines.map((line) => {
      return line
        .split("|")
        .map((cell) => cell.trim())
        .filter((_, index, arr) => index !== 0 && index !== arr.length - 1);
    });

    const filteredAoa = aoa.filter((row) => !row.every((cell) => /^[-:]+$/.test(cell)));

    const fileId = activeMessage.threadId || activeMessage.id.substring(0, 6);

    const ws = XLSX.utils.aoa_to_sheet(filteredAoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `HACS_Data_${fileId}.xlsx`);
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      elevation={2}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{
        zIndex: 1600,
        "& .MuiPaper-root": {
          borderRadius: "12px",
          minWidth: "200px",
          boxShadow: "0px 4px 14px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      <MenuItem onClick={handleDownloadPDF}>
        <PictureAsPdfIcon sx={{ mr: 1, color: "#d32f2f" }} fontSize="small" />
        <Typography variant="body2">Download as PDF</Typography>
      </MenuItem>

      {activeMessage && hasMarkdownTable(activeMessage.text) && (
        <MenuItem onClick={handleDownloadXLSX}>
          <TableViewIcon sx={{ mr: 1, color: "#2e7d32" }} fontSize="small" />
          <Typography variant="body2">Download as XLSX</Typography>
        </MenuItem>
      )}
    </Menu>
  );
};

export default MessageMenu;